<template>
  <div class="teacher-page d-flex">
    <aside class="side-nav" :class="{ collapsed: sidebarCollapsed }">
      <div class="side-head">
        <h4 class="mb-0" v-show="!sidebarCollapsed">教师端</h4>
        <button class="collapse-btn" type="button" @click="toggleSidebar">{{ sidebarCollapsed ? '>>' : '<<' }}</button>
      </div>
      <button class="sub-btn" type="button" @click="goQuestionManage"><span class="nav-text">题库管理</span></button>
      <button class="sub-btn" type="button" @click="goPaperManage"><span class="nav-text">试卷管理</span></button>
      <button class="sub-btn" type="button" @click="goExamManage"><span class="nav-text">考试管理</span></button>
      <button class="sub-btn" type="button" @click="goGradingManage"><span class="nav-text">阅卷管理</span></button>
      <button class="sub-btn" type="button" @click="goExamRecordManage"><span class="nav-text">考生试卷管理</span></button>
      <button class="sub-btn" type="button" @click="goProctor"><span class="nav-text">监考系统</span></button>
      <button class="sub-btn active" type="button"><span class="nav-text">学生用户管理</span></button>
      <div class="mt-auto">
        <button class="btn btn-sm btn-light w-100 mb-2" type="button" @click="logout">退出登录</button>
      </div>
    </aside>

    <main class="content-area">
      <header class="topbar">
        <h2>学生用户管理</h2>
        <div class="actions">
          <span class="text-muted small">当前账号：{{ currentUser.username || '教师' }}</span>
        </div>
      </header>

      <section class="card-box">
        <h4>查询学生</h4>
        <p class="small text-muted mb-3">按条件筛选学生账号，结果在下方列表中展示。</p>
        <div class="row g-3 mb-3 align-items-end">
          <div class="col-md-3">
            <div class="field">
              <label class="field-label">关键字</label>
              <input v-model="userFilters.keyword" class="form-control" placeholder="账号 / 姓名 / 手机号" @keyup.enter="runUserQuery" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">状态</label>
              <select v-model="userFilters.status" class="form-select">
                <option value="">全部状态</option>
                <option value="1">正常</option>
                <option value="0">禁用</option>
              </select>
            </div>
          </div>
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">省份</label>
              <select v-model="userFilters.province" class="form-select">
                <option value="">全部省份</option>
                <option v-for="p in provinceOptions" :key="'uf-p-' + p" :value="p">{{ p }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">地市</label>
              <select v-model="userFilters.city" class="form-select" :disabled="!userFilters.province">
                <option value="">全部地市</option>
                <option v-for="c in filterCityOptions" :key="'uf-c-' + c" :value="c">{{ c }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-2">
            <button class="btn btn-primary w-100" type="button" @click="runUserQuery">查询</button>
          </div>
        </div>

        <div class="result-shell">
          <table class="table table-striped align-middle modern-table mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>账号</th>
                <th>姓名</th>
                <th>角色</th>
                <th>手机号</th>
                <th>省份</th>
                <th>地市</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in users" :key="u.id">
                <td>{{ u.id }}</td>
                <td>{{ u.username }}</td>
                <td>{{ u.realName || '-' }}</td>
                <td>学生</td>
                <td>{{ u.phone || '-' }}</td>
                <td>{{ u.province || '-' }}</td>
                <td>{{ u.city || '-' }}</td>
                <td>{{ Number(u.status) === 1 ? '正常' : '禁用' }}</td>
                <td>
                  <button class="btn btn-sm btn-light me-2" type="button" @click="toggleUserStatus(u)">
                    {{ Number(u.status) === 1 ? '禁用' : '启用' }}
                  </button>
                  <button class="btn btn-sm btn-warning me-2" type="button" @click="openUserEditModal(u)">编辑</button>
                  <button class="btn btn-sm btn-primary" type="button" @click="openResetPassword(u)">重置密码</button>
                </td>
              </tr>
              <tr v-if="users.length === 0">
                <td colspan="9" class="text-muted">暂无学生数据，请调整筛选条件后查询</td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="userTotalElements > 0"
            class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-2 border-top"
          >
            <div class="small text-muted">
              共 {{ userTotalElements }} 条，每页 {{ userPageSize }} 条，第 {{ userPage + 1 }} / {{ userTotalPages }} 页
            </div>
            <div class="btn-group">
              <button type="button" class="btn btn-sm btn-light" :disabled="userPage <= 0" @click="goUserPage(0)">首页</button>
              <button type="button" class="btn btn-sm btn-light" :disabled="userPage <= 0" @click="goUserPage(userPage - 1)">上一页</button>
              <button type="button" class="btn btn-sm btn-light" :disabled="userPage >= userTotalPages - 1" @click="goUserPage(userPage + 1)">下一页</button>
              <button type="button" class="btn btn-sm btn-light" :disabled="userPage >= userTotalPages - 1" @click="goUserPage(userTotalPages - 1)">末页</button>
            </div>
          </div>
        </div>
      </section>

      <section class="card-box mt-4">
        <h4>新增学生</h4>
        <div class="row g-3 mb-3 align-items-end">
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">账号 <span class="text-danger">*</span></label>
              <input v-model.trim="createForm.username" class="form-control" placeholder="登录账号" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">姓名</label>
              <input v-model.trim="createForm.realName" class="form-control" placeholder="真实姓名" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">手机号</label>
              <input v-model.trim="createForm.phone" class="form-control" placeholder="手机号" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">密码 <span class="text-danger">*</span></label>
              <input v-model="createForm.password" type="password" class="form-control" placeholder="登录密码" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="field">
              <label class="field-label">角色</label>
              <input class="form-control" value="学生" disabled />
            </div>
          </div>
        </div>
        <div class="row g-3 mb-3 align-items-end">
          <div class="col-md-3">
            <div class="field">
              <label class="field-label">省份</label>
              <select v-model="createForm.province" class="form-select">
                <option value="">请选择省份</option>
                <option v-for="p in provinceOptions" :key="'cf-p-' + p" :value="p">{{ p }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-3">
            <div class="field">
              <label class="field-label">地市</label>
              <select v-model="createForm.city" class="form-select" :disabled="!createForm.province">
                <option value="">请选择地市</option>
                <option v-for="c in createCityOptions" :key="'cf-c-' + c" :value="c">{{ c }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-2">
            <button class="btn btn-success w-100" type="button" @click="createUser">提交新增</button>
          </div>
          <div class="col-md-2">
            <button class="btn btn-light w-100" type="button" @click="resetCreateUserForm">清空表单</button>
          </div>
        </div>
      </section>

      <section class="card-box mt-4">
        <h4>批量导入学生</h4>
        <p class="small text-muted mb-3">
          模板不含角色列；系统导入时一律强制为<strong>学生</strong>账号，无法创建教师或管理员。
        </p>
        <div class="row g-3 align-items-end">
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">选择 Excel 文件</label>
              <input ref="userExcelInput" type="file" class="form-control" accept=".xlsx" @change="onUserExcelChange" />
            </div>
          </div>
          <div class="col-md-3">
            <button class="btn btn-primary w-100" type="button" @click="importUsersByExcel">Excel批量导入</button>
          </div>
          <div class="col-md-3">
            <button class="btn btn-light w-100" type="button" @click="downloadUserTemplate">下载导入模板</button>
          </div>
        </div>
      </section>
    </main>

    <div v-if="showUserEditModal" class="modal-mask" @click.self="closeUserEditModal">
      <div class="modal-panel">
        <h5 class="mb-3">编辑学生</h5>
        <div class="row g-3">
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">账号</label>
              <input v-model="editingUser.username" class="form-control" disabled />
            </div>
          </div>
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">姓名</label>
              <input v-model="editingUser.realName" class="form-control" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">角色</label>
              <input class="form-control" value="学生" disabled />
            </div>
          </div>
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">状态</label>
              <select v-model.number="editingUser.status" class="form-select">
                <option :value="1">正常</option>
                <option :value="0">禁用</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">手机号</label>
              <input v-model="editingUser.phone" class="form-control" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">省份</label>
              <select v-model="editingUser.province" class="form-select">
                <option value="">请选择省份</option>
                <option v-for="p in provinceOptions" :key="'eu-p-' + p" :value="p">{{ p }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">地市</label>
              <select v-model="editingUser.city" class="form-select" :disabled="!editingUser.province">
                <option value="">请选择地市</option>
                <option v-for="c in editCityOptions" :key="'eu-c-' + c" :value="c">{{ c }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="field">
              <label class="field-label">新密码（可空）</label>
              <input v-model="editingUser.password" type="password" class="form-control" placeholder="留空则不修改密码" />
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="btn btn-light" type="button" @click="closeUserEditModal">取消</button>
          <button class="btn btn-primary" type="button" @click="saveUserEdit">保存修改</button>
        </div>
      </div>
    </div>

    <div v-if="showResetModal" class="modal-mask" @click.self="closeResetModal">
      <div class="modal-panel">
        <h5 class="mb-3">重置密码 · {{ resetTarget.username || '-' }}</h5>
        <div class="field mb-3">
          <label class="field-label">新密码 <span class="text-danger">*</span></label>
          <input v-model="resetPassword" type="password" class="form-control" placeholder="请输入新密码" />
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-light" type="button" @click="closeResetModal">取消</button>
          <button class="btn btn-primary" type="button" @click="submitResetPassword">确认重置</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { provinceCityMap, provinceOptions } from '../utils/chinaRegions'

export default {
  name: 'TeacherStudentUserManageView',
  data() {
    return {
      sidebarCollapsed: false,
      currentUser: {},
      users: [],
      userPage: 0,
      userPageSize: 10,
      userTotalElements: 0,
      userTotalPages: 1,
      userFilters: { keyword: '', status: '', province: '', city: '' },
      createForm: { username: '', realName: '', phone: '', password: '', province: '', city: '' },
      userExcelFile: null,
      showUserEditModal: false,
      editingUser: {
        id: null,
        username: '',
        realName: '',
        status: 1,
        phone: '',
        province: '',
        city: '',
        password: ''
      },
      showResetModal: false,
      resetTarget: { id: null, username: '' },
      resetPassword: ''
    }
  },
  computed: {
    provinceOptions() {
      return provinceOptions
    },
    filterCityOptions() {
      return provinceCityMap[this.userFilters.province] || []
    },
    createCityOptions() {
      return provinceCityMap[this.createForm.province] || []
    },
    editCityOptions() {
      return provinceCityMap[this.editingUser.province] || []
    }
  },
  watch: {
    'userFilters.province'(v) {
      const list = provinceCityMap[v] || []
      if (!this.userFilters.city || !list.includes(this.userFilters.city)) {
        this.userFilters.city = ''
      }
    },
    'createForm.province'(v) {
      const list = provinceCityMap[v] || []
      if (!this.createForm.city || !list.includes(this.createForm.city)) {
        this.createForm.city = ''
      }
    },
    'editingUser.province'(v) {
      const list = provinceCityMap[v] || []
      if (!this.editingUser.city || !list.includes(this.editingUser.city)) {
        this.editingUser.city = ''
      }
    }
  },
  created() {
    try {
      this.currentUser = JSON.parse(localStorage.getItem('exam_user') || '{}')
    } catch (e) {
      this.currentUser = {}
    }
    this.loadUsers()
  },
  methods: {
    authHeaders(extra = {}) {
      let role = 'teacher'
      let userId = ''
      let username = ''
      try {
        const u = JSON.parse(localStorage.getItem('exam_user') || '{}')
        role = u.role || 'teacher'
        userId = u.id != null ? String(u.id) : ''
        username = u.username || ''
      } catch (e) {
        void e
      }
      return {
        'Content-Type': 'application/json',
        'X-Role': role,
        'X-User-Id': userId,
        'X-Username': username,
        'X-CSRF-Token': role === 'admin' ? 'exam-ui' : 'teacher-ui',
        ...extra
      }
    },
    authHeadersNoJson() {
      const h = this.authHeaders()
      delete h['Content-Type']
      return h
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    logout() {
      localStorage.removeItem('exam_user')
      window.location.hash = '#/'
    },
    goQuestionManage() {
      window.location.hash = '#/teacher/questions'
    },
    goPaperManage() {
      window.location.hash = '#/teacher/papers'
    },
    goExamManage() {
      localStorage.setItem('teacher_active_menu', 'exam')
      window.location.hash = '#/teacher/papers'
    },
    goGradingManage() {
      localStorage.setItem('teacher_active_menu', 'grading')
      window.location.hash = '#/teacher/papers'
    },
    goExamRecordManage() {
      window.location.hash = '#/teacher/exam-records'
    },
    goProctor() {
      window.location.hash = '#/teacher/proctor'
    },
    runUserQuery() {
      this.userPage = 0
      this.loadUsers()
    },
    resetCreateUserForm() {
      this.createForm = { username: '', realName: '', phone: '', password: '', province: '', city: '' }
    },
    goUserPage(p) {
      const max = Math.max(0, this.userTotalPages - 1)
      const next = Math.max(0, Math.min(Number(p) || 0, max))
      if (next === this.userPage) return
      this.userPage = next
      this.loadUsers()
    },
    loadUsers() {
      const params = new URLSearchParams({
        page: String(this.userPage),
        size: String(this.userPageSize)
      })
      if (this.userFilters.keyword) params.append('keyword', this.userFilters.keyword)
      if (this.userFilters.status) params.append('status', this.userFilters.status)
      if (this.userFilters.province) params.append('province', this.userFilters.province)
      if (this.userFilters.city) params.append('city', this.userFilters.city)
      fetch(`/api/teacher/student-users?${params.toString()}`, { headers: this.authHeaders() })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '加载失败')
            return
          }
          const page = res.data || {}
          this.users = Array.isArray(page.content) ? page.content : []
          this.userTotalElements = Number(page.totalElements || 0)
          this.userTotalPages = Math.max(1, Number(page.totalPages || 1))
          if (page.number != null) this.userPage = Number(page.number)
        })
        .catch(() => window.alert('加载学生列表失败'))
    },
    createUser() {
      const username = String(this.createForm.username || '').trim()
      const password = String(this.createForm.password || '')
      if (!username) {
        window.alert('请填写账号')
        return
      }
      if (!password) {
        window.alert('请填写密码')
        return
      }
      fetch('/api/teacher/student-users', {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({
          username,
          password,
          realName: String(this.createForm.realName || '').trim(),
          phone: String(this.createForm.phone || '').trim(),
          province: this.createForm.province || '',
          city: this.createForm.city || '',
          role: 'student'
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            window.alert('新增学生成功')
            this.resetCreateUserForm()
            this.userPage = 0
            this.loadUsers()
            return
          }
          window.alert(res.message || '新增失败')
        })
        .catch(() => window.alert('新增失败，请稍后重试'))
    },
    openUserEditModal(u) {
      this.editingUser = {
        id: u.id,
        username: u.username || '',
        realName: u.realName || '',
        status: Number(u.status) === 1 ? 1 : 0,
        phone: u.phone || '',
        province: u.province || '',
        city: u.city || '',
        password: ''
      }
      this.showUserEditModal = true
    },
    closeUserEditModal() {
      this.showUserEditModal = false
    },
    saveUserEdit() {
      if (!this.editingUser.id) return
      fetch(`/api/teacher/student-users/${this.editingUser.id}`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify({
          realName: this.editingUser.realName,
          phone: this.editingUser.phone,
          province: this.editingUser.province,
          city: this.editingUser.city,
          status: this.editingUser.status,
          password: this.editingUser.password || '',
          role: 'student'
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            this.closeUserEditModal()
            this.loadUsers()
            return
          }
          window.alert(res.message || '保存失败')
        })
        .catch(() => window.alert('保存失败'))
    },
    toggleUserStatus(u) {
      fetch(`/api/teacher/student-users/${u.id}`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify({
          status: Number(u.status) === 1 ? 0 : 1,
          role: 'student'
        })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '更新状态失败')
            return
          }
          this.loadUsers()
        })
        .catch(() => window.alert('更新状态失败'))
    },
    openResetPassword(u) {
      this.resetTarget = { id: u.id, username: u.username || '' }
      this.resetPassword = ''
      this.showResetModal = true
    },
    closeResetModal() {
      this.showResetModal = false
      this.resetPassword = ''
    },
    submitResetPassword() {
      if (!this.resetTarget.id) return
      const pwd = String(this.resetPassword || '').trim()
      if (!pwd) {
        window.alert('请输入新密码')
        return
      }
      fetch(`/api/teacher/student-users/${this.resetTarget.id}/reset-password`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ password: pwd })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code === 200) {
            window.alert('密码已重置')
            this.closeResetModal()
            return
          }
          window.alert(res.message || '重置失败')
        })
        .catch(() => window.alert('重置失败'))
    },
    onUserExcelChange(e) {
      this.userExcelFile = (e.target.files && e.target.files[0]) || null
    },
    importUsersByExcel() {
      if (!this.userExcelFile) {
        window.alert('请先选择Excel文件')
        return
      }
      const form = new FormData()
      form.append('file', this.userExcelFile)
      fetch('/api/teacher/student-users/import-excel', {
        method: 'POST',
        headers: this.authHeadersNoJson(),
        body: form
      })
        .then((r) => r.json())
        .then((res) => {
          const tip = res.code === 200
            ? (res.data || res.message || '导入完成')
            : (res.message || res.data || '导入失败')
          window.alert(String(tip))
          if (res.code === 200) {
            this.userPage = 0
            this.loadUsers()
            if (this.$refs.userExcelInput) this.$refs.userExcelInput.value = ''
            this.userExcelFile = null
          }
        })
        .catch(() => window.alert('导入失败'))
    },
    downloadUserTemplate() {
      fetch('/api/teacher/student-users/import-template', { headers: this.authHeadersNoJson() })
        .then(async (r) => {
          if (!r.ok) {
            window.alert('下载模板失败')
            return
          }
          const blob = await r.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'teacher_student_import_template.xlsx'
          a.click()
          URL.revokeObjectURL(url)
        })
        .catch(() => window.alert('下载模板失败'))
    }
  }
}
</script>

<style scoped>
.teacher-page {
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(160deg, #eef5ff 0%, #f7fbff 48%, #eef2ff 100%);
}
.side-nav {
  width: 240px;
  min-height: calc(100vh - 40px);
  margin-right: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(63, 108, 199, 0.1);
  flex-shrink: 0;
}
.side-nav.collapsed {
  width: 76px;
}
.side-nav.collapsed .nav-text {
  display: none;
}
.side-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.collapse-btn {
  border: 1px solid #c8dcff;
  background: #edf4ff;
  color: #2f5da5;
  border-radius: 8px;
  padding: 2px 8px;
  font-weight: 700;
}
.sub-btn {
  text-align: left;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 10px;
  padding: 10px 12px;
  color: #355b9f;
  font-weight: 600;
}
.sub-btn:hover {
  background: #edf4ff;
}
.sub-btn.active {
  background: linear-gradient(120deg, #2f6feb, #5ca2ff);
  color: #fff;
}
.content-area {
  flex: 1;
  min-width: 0;
}
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  border: 1px solid #d8e6ff;
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
}
.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.card-box {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  border: 1px solid #d8e6ff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}
.field-label {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #4a628c;
  line-height: 1.4;
}
.result-shell {
  border: 1px solid #dbe8ff;
  border-radius: 12px;
  overflow: auto;
  background: linear-gradient(180deg, #fff 0%, #f8fbff 100%);
  padding: 4px;
}
.modern-table thead th {
  background: #edf4ff !important;
  color: #2a4f90 !important;
}
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(18, 36, 76, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}
.modal-panel {
  width: min(720px, 100%);
  max-height: calc(100vh - 40px);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #d8e6ff;
  padding: 18px;
  box-shadow: 0 20px 50px rgba(35, 72, 150, 0.25);
}
@media (max-width: 992px) {
  .teacher-page {
    flex-direction: column;
  }
  .side-nav {
    width: 100%;
    min-height: auto;
    margin-right: 0;
    margin-bottom: 12px;
  }
}
</style>
